// @flow

import React, { PureComponent } from 'react';
import { i18n } from 'shared/utils';
import { fetchQuery, graphql } from 'relay-runtime';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { FieldGroup } from 'shared/components/form';

type Props = {
  name?: string,
  rawField?: boolean,
  label?: ?string,
  error?: ?string,
  queryVariables?: ?Object,
  excludeIds?: Array<string>,
  creatingNewRecord?: boolean,
  excludeOperators?: boolean,
};

type State = {
  fetchedOptions: Array<Object>,
};

const QUERY = graphql`
  query SelectUserFieldQuery(
    $sortBy: UserSort
    $companyUserRoles: [CompanyUserRole!]
    $search: String
  ) {
    users(
      first: 50
      sortBy: $sortBy
      companyUserRoles: $companyUserRoles
      search: $search
    ) {
      edges {
        node {
          id
          firstName
          lastName
          userNumber
        }
      }
    }
  }
`;

class SelectUserField extends PureComponent<Props, State> {
  static defaultProps = {
    name: undefined,
    rawField: false,
    label: undefined,
    error: undefined,
    excludeIds: [],
    queryVariables: {},
    creatingNewRecord: false,
    excludeOperators: true,
  };

  mounted = false;

  state = {
    fetchedOptions: [],
  };

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getData = async (inputValue: string) => {
    const { queryVariables, excludeIds, excludeOperators } = this.props;
    const companyUserRoles = ['USER', 'ADMIN'];

    if (!excludeOperators) {
      companyUserRoles.push('WORKER');
    }

    try {
      const results = await fetchQuery(relayEnvironment, QUERY, {
        search: inputValue,
        companyUserRoles,
        ...queryVariables,
      });

      const options = this.massageData(results.users.edges || [], excludeIds);

      if (this.mounted) {
        this.setState({
          fetchedOptions: options,
        });
      }

      return options;
    } catch (e) {
      return [];
    }
  };

  /**
   * Massage records to be ReactSelect-friendly
   * @param {Array<Object>} records
   * @param {Array<string | number>} excludeIds
   * @return {Array<Object>}
   */
  massageData = (
    records: Array<Object>,
    excludeIds?: Array<string> = [],
  ): Array<Object> => {
    return (
      records
        // filter out specified ids
        .filter(a => !a.node || excludeIds.indexOf(a.node.id.toString()) < 0)
        .map(a => ({
          data: a.node,
          value: a.node.id,
          label: a.node.firstName + ' ' + a.node.lastName,
        }))
    );
  };

  render() {
    const {
      name,
      rawField,
      label,
      error,
      creatingNewRecord,
      ...fieldProps
    } = this.props;
    const { fetchedOptions } = this.state;

    return (
      <FieldGroup
        name={name}
        rawField={rawField || false}
        label={label !== undefined ? label : i18n.t('User')}
        error={error}
        message={
          creatingNewRecord ? i18n.t(`A new user will be created`) : null
        }
        clearable
        placeholder={i18n.t(`Search for User`)}
        {...fieldProps}
        options={fetchedOptions}
        type="select"
        loadOptions={this.getData}
        defaultOptions
      />
    );
  }
}

export default SelectUserField;
